import React, { useMemo, useState } from 'react';
import { Button, Form, message, Modal } from 'antd';
import cx from 'classnames';
import dayjs from 'dayjs';
import {
    CheckCircleOutlined,
    CheckOutlined,
    ClockCircleOutlined,
    PlusOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import {
    CustomEhrModal,
    EhrConnectFlow,
    PF_SETUP_LABEL,
    connectionLost,
    isPfSettingUp,
    neverConnected,
    notConnectedCopy,
    pfNeedsNewCode,
    reconnectsWithCredentials,
} from 'components/EhrConnect';
import { deleteEhrConnection, verifyEhrConnection } from 'api/ehr';
import useEhrConnections from 'hooks/useEhrConnections';
import useEhrOptions from 'hooks/useEhrOptions';
import EhrBadge from '../EhrBadge';
import HiddenFormField from '../HiddenFormField';
import { ehrByName } from '../../constants';
import { fetchOnboardingVo, STEP_API, stepValuesToDto } from '../../billingReviewData';
import { formatTodayStartDate } from '../../utils';
import { ClaimScopeField, ClaimStartFields, SCOPE_OPTIONS, START_OPTIONS } from '../../steps/StepEhrScope';
import s from './s.module.less';

type Props = {
    onSaveSuccess?: () => void;
    // Managed billing 里对所有人关掉的 EHR：行上标 Not billed（只读，开关在 Settings › Payment）
    notBilledEhrs?: string[];
};

// Settings 的 EHR & billing scope tab：已连接 EHR 的只读连接行（原型 V3 ehrConnRow）。
// 是否计费只在 Settings › Payment 的 Managed billing 里改，这里只标 Not billed，不再有勾选框；
// Disconnect 真实断开连接。连接/重连复用 EhrConnectFlow，连上后默认并入 billingEhrs。
export default function EhrScopeSettings({ onSaveSuccess, notBilledEhrs }: Props) {
    const form = Form.useFormInstance();
    const billingEhrs: string[] = Form.useWatch(['ehr', 'billingEhrs'], form) || [];
    const claimScope = Form.useWatch(['ehr', 'claimScope'], form);
    const [connections, , refreshConnections] = useEhrConnections();
    const [ehrOptions] = useEhrOptions();
    // 正在走 connect / re-verify 流程的 EHR 名
    const [flowEhr, setFlowEhr] = useState<string | null>(null);
    // 本次 flow 是 Reconnect（密码型 EHR 直接从 step 2 重新输入）
    const [flowReconnect, setFlowReconnect] = useState(false);
    const [reconnectingEhr, setReconnectingEhr] = useState<string | null>(null);
    // "My EHR isn't listed"（自建/未收录 EHR 登记）弹窗
    const [customOpen, setCustomOpen] = useState(false);
    // 点 Upcoming 卡片打开 Tell us about your EHR 时预填的名称（My EHR isn't listed 为空）
    const [customName, setCustomName] = useState('');
    const [scopeEditing, setScopeEditing] = useState(false);
    const [scopeSnap, setScopeSnap] = useState<string | undefined>(undefined);
    const [scopeSaving, setScopeSaving] = useState(false);
    const claimStartMode = Form.useWatch(['ehr', 'claimStartMode'], form);
    const claimStartDate = Form.useWatch(['ehr', 'claimStartDate'], form);
    // today 模式后端回的固定起始日（只读，hydrate / 保存后重拉写入）
    const claimTodayStartDate: string | undefined = Form.useWatch(['ehr', 'claimTodayStartDate'], form);
    const [startEditing, setStartEditing] = useState(false);
    const [startSnap, setStartSnap] = useState<{ mode?: string; date?: unknown }>({});
    const [startSaving, setStartSaving] = useState(false);

    const availableEhrs = useMemo(() => ehrOptions.filter((e) => !e.upcoming), [ehrOptions]);
    const upcomingEhrs = useMemo(() => ehrOptions.filter((e) => e.upcoming), [ehrOptions]);

    // 保存整个 ehr slice（claimStart 等未展示字段原样保留）
    const saveEhrSlice = async () => {
        const ehrValues = form.getFieldsValue(true)?.ehr || {};
        const dto = stepValuesToDto('claim-scope', { ehr: ehrValues });
        const res: any = await STEP_API['claim-scope'].save(dto);
        if (res?.error) throw new Error(res.error);
        onSaveSuccess?.();
    };

    // 真实连接状态：connected = 连接成功 / setup = PF 异步开通中（列在上方连接行里）/
    // error = 连上过、现已失效（可重连）/ agent = 有连接记录但从没连上过（原型 Agent not added，
    // 不能当 Connection lost）/ none = 无连接记录
    const connStatusOf = (name: string): 'connected' | 'setup' | 'error' | 'agent' | 'none' => {
        const c = connections.find((item) => item.ehrSystem === name);
        if (!c) return 'none';
        if (c.connectionStatus === 'connected') return 'connected';
        if (isPfSettingUp(c)) return 'setup';
        return c.hasSuccessConnected ? 'error' : 'agent';
    };

    const openFlow = (name: string, reconnect = false) => {
        setFlowReconnect(reconnect);
        setFlowEhr(name);
    };

    // 原型 V3 Reconnect：密码型（含 PF 重新交 PAC）回 step 2 重新输入；邀请型一键重新验证
    const handleReconnect = async (name: string) => {
        if (reconnectsWithCredentials(name)) {
            openFlow(name, true);
            return;
        }
        setReconnectingEhr(name);
        const res: any = await verifyEhrConnection({ ehrSystem: name });
        setReconnectingEhr(null);
        const data = res?.data?.data;
        if (res && !res.error && (data?.verified || data?.connectionStatus === 'connected')) {
            message.success(`${name} reconnected`);
        } else if (res && !res.error) {
            message.error(data?.error || `Could not reach ${name}. Check that the agent account is still active.`);
        }
        refreshConnections();
    };

    // 确认弹窗（对照原型）：无图标、大标题、右对齐大圆角按钮
    const confirmModalProps = {
        icon: null,
        className: s.confirmModal,
        okButtonProps: { danger: true, type: 'primary' as const },
    };

    // 真实断开连接（删除 connection 记录，与 dashboard 的 EhrConnectModal 一致）；
    // 不动 billingEhrs——断开后行从列表里消失，重连时沿用原计费范围
    const handleDisconnect = (name: string) => {
        const conn = connections.find((c) => c.ehrSystem === name);
        if (!conn) return;
        Modal.confirm({
            ...confirmModalProps,
            title: `Disconnect ${name}?`,
            content: `Klarity will stop billing claims from ${name}. Existing claims are not affected, and you can reconnect anytime.`,
            okText: 'Disconnect',
            onOk: async () => {
                const res: any = await deleteEhrConnection(conn.id);
                if (res && !res.error) {
                    message.success(`${name} disconnected`);
                    refreshConnections();
                }
                // 失败已由全局 toast 展示
            },
        });
    };

    // connect / 重连成功：加入计费范围并保存，刷新连接列表
    const handleFlowSuccess = async () => {
        const name = flowEhr!;
        setFlowEhr(null);
        setFlowReconnect(false);
        refreshConnections();
        if (!billingEhrs.includes(name)) {
            form.setFieldValue(['ehr', 'billingEhrs'], [...billingEhrs, name]);
            try {
                await saveEhrSlice();
            } catch (e) {
                // 错误已由全局 toast 展示
            }
        }
    };

    // custom EHR 登记成功：与 connect 成功同口径——默认勾进计费范围并保存
    // （连接列表由 CustomEhrForm 内部 refresh 广播过来）
    const handleCustomSubmitted = async (name: string) => {
        setCustomOpen(false);
        if (!billingEhrs.includes(name)) {
            form.setFieldValue(['ehr', 'billingEhrs'], [...billingEhrs, name]);
            try {
                await saveEhrSlice();
            } catch (e) {
                // 错误已由全局 toast 展示
            }
        }
    };

    const openScopeEdit = () => {
        setScopeSnap(claimScope);
        setScopeEditing(true);
    };

    const cancelScopeEdit = () => {
        form.setFieldValue(['ehr', 'claimScope'], scopeSnap);
        setScopeEditing(false);
    };

    const saveScopeEdit = async () => {
        setScopeSaving(true);
        try {
            await saveEhrSlice();
            setScopeEditing(false);
            message.success('Changes saved.');
        } catch (e) {
            // 错误已由全局 toast 展示，保持编辑态供用户修正
        } finally {
            setScopeSaving(false);
        }
    };

    const openStartEdit = () => {
        setStartSnap({ mode: claimStartMode, date: claimStartDate });
        setStartEditing(true);
    };

    const cancelStartEdit = () => {
        form.setFieldValue(['ehr', 'claimStartMode'], startSnap.mode);
        form.setFieldValue(['ehr', 'claimStartDate'], startSnap.date);
        setStartEditing(false);
    };

    const saveStartEdit = async () => {
        setStartSaving(true);
        try {
            await saveEhrSlice();
            setStartEditing(false);
            message.success('Changes saved.');
            // today 的起始日由后端生成：保存后重拉一次，specific → today 切换后才有日期可显示
            const vo = await fetchOnboardingVo();
            const c = vo?.claimSettings;
            form.setFieldValue(
                ['ehr', 'claimTodayStartDate'],
                c?.claimStartMode === 'today' ? c.claimStartDate || undefined : undefined,
            );
        } catch (e) {
            // 错误已由全局 toast 展示，保持编辑态供用户修正
        } finally {
            setStartSaving(false);
        }
    };

    // 连接成功 + custom（琥珀 Not supported yet，长期 pending 等内部对接）都入列，按后端返回顺序展示；
    // PF 异步开通中也入列：标进度，需要新 PAC 时给 Re-enter code（PAC 每周变）
    // 从没连上过的也入列（原型：琥珀 Agent not added + Show steps）；
    // 连上过又断了的也入列（原型 ehrConnRow err：红色 Connection lost + Reconnect）
    const listed = connections.filter(
        (c) =>
            c.connectionStatus === 'connected' ||
            c.isCustom ||
            isPfSettingUp(c) ||
            neverConnected(c) ||
            connectionLost(c),
    );
    // 后端可能给多条连接都标 isPrimary=1：只在第一条上标 Primary
    const primaryName = listed.find((c) => c.isPrimary === 1)?.ehrSystem;
    const connRows = listed.map((c) => {
        const name = c.ehrSystem;
        const settingUp = c.connectionStatus !== 'connected' && isPfSettingUp(c);
        const noAgent = neverConnected(c);
        const lost = connectionLost(c);
        const pending = !!c.isCustom || settingUp || noAgent;
        // 原型：Managed billing 关掉的 EHR 仍连接，行置灰 + Not billed
        const off = !pending && !lost && !!notBilledEhrs?.includes(name);
        let status: React.ReactNode = (
            <span className={cx(s.connStatus, s.connStatusOk)}>
                <span className={s.connDot} />
                Connected
            </span>
        );
        if (c.isCustom) {
            // 原型 V3：未收录 EHR 提交时已确认加了 agent 邮箱，行上同时标 Invite pending
            status = (
                <>
                    <span className={s.connTagMuted}>Not supported yet</span>
                    <span className={s.connTagPending}>Invite pending</span>
                </>
            );
        } else if (settingUp) {
            status = <span className={s.connTagPending}>{PF_SETUP_LABEL[c.setupStatus!]}</span>;
        } else if (noAgent) {
            status = <span className={s.connTagPending}>{notConnectedCopy(name).tag}</span>;
        } else if (lost) {
            status = (
                <span className={cx(s.connStatus, s.connStatusErr)}>
                    <span className={s.connDot} />
                    Error
                </span>
            );
        }
        let sub: string | null = null;
        if (settingUp) sub = c.setupMessage || null;
        else if (noAgent) sub = notConnectedCopy(name).sub;
        else if (lost) sub = 'Connection lost. Reconnect to resume billing.';
        else if (off) sub = 'Still connected. Klarity is not submitting claims for this EHR.';
        else if (!c.isCustom && c.createdAt) sub = `Connected since ${dayjs(c.createdAt).format('MMM D, YYYY')}`;
        return (
            <div key={name} className={cx(s.connRow, off && s.connRowOff)}>
                <div className={s.connLeft}>
                    {lost && <WarningOutlined className={s.connIconErr} />}
                    {!lost && pending && <ClockCircleOutlined className={s.connIconPending} />}
                    {!lost && !pending && <CheckCircleOutlined className={s.connIconOk} />}
                    <div>
                        <div className={s.connName}>
                            {name}
                            {name === primaryName && <span className={s.primaryTag}>Primary</span>}
                            {off && <span className={s.notBilledTag}>Not billed</span>}
                        </div>
                        {sub && <div className={cx(s.connSince, lost && s.connSinceErr)}>{sub}</div>}
                    </div>
                </div>
                <div className={s.connRight}>
                    {settingUp && pfNeedsNewCode(c) && (
                        <Button type="link" className={s.connLink} onClick={() => handleReconnect(name)}>
                            Re-enter code
                        </Button>
                    )}
                    {/* 原型：没连上时操作只有 Show steps（回到加 agent 的步骤指引）；
                        断了只有 Reconnect（设置页不给 Disconnect），走与下方卡片相同的重连 */}
                    {noAgent && (
                        <Button type="primary" onClick={() => openFlow(name)}>
                            Show steps
                        </Button>
                    )}
                    {lost && (
                        <Button type="primary" loading={reconnectingEhr === name} onClick={() => handleReconnect(name)}>
                            Reconnect
                        </Button>
                    )}
                    {!noAgent && !lost && (
                        <Button
                            type="link"
                            className={cx(s.connLink, s.connLinkDanger)}
                            onClick={() => handleDisconnect(name)}
                        >
                            Disconnect
                        </Button>
                    )}
                    {status}
                </div>
            </div>
        );
    });

    const currentScope = SCOPE_OPTIONS.find((o) => o.value === claimScope) || SCOPE_OPTIONS[0];
    const currentStart = START_OPTIONS.find((o) => o.value === claimStartMode) || START_OPTIONS[0];
    // 读态标题显示具体日期（与 review 页一致）：specific → "From {date}"，today → "Starting from {固定起始日}"
    let startReadTitle = currentStart.title;
    let startReadDesc = currentStart.desc;
    if (claimStartMode === 'specific' && claimStartDate) {
        startReadTitle = `From ${dayjs(claimStartDate).format('MMM D, YYYY')}`;
    } else if (claimStartMode === 'today' && claimTodayStartDate) {
        startReadTitle = `Starting from ${formatTodayStartDate(claimTodayStartDate)}`;
        startReadDesc = 'Only encounters from that date onward.';
    }

    return (
        <div className={s.ehrScope}>
            {/* 注册辅助字段：useWatch 只对已注册字段生效 */}
            <Form.Item name={['ehr', 'billingEhrs']} noStyle>
                <HiddenFormField />
            </Form.Item>
            <Form.Item name={['ehr', 'claimTodayStartDate']} noStyle>
                <HiddenFormField />
            </Form.Item>

            <div className={s.connTitle}>EHR connections</div>
            <div className={s.connList}>
                {connRows.length ? connRows : <div className={s.connEmpty}>No EHR connected yet.</div>}
            </div>

            <div className={s.moreTitle}>Deploy agents to more EHR accounts</div>
            <div className={s.moreSub}>Bring managed billing to your other EHR accounts.</div>
            <div className={s.ehrGrid}>
                {availableEhrs.map((ehr) => {
                    // 按真实连接状态渲染：connected 不可点；error 可点进入重连；none 可点进入连接
                    const status = connStatusOf(ehr.name);
                    if (status === 'connected') {
                        return (
                            <div key={ehr.name} className={cx(s.ehrCard, s.ehrCardConnected)}>
                                <EhrBadge ehr={ehrByName(ehr.name)} />
                                <span className={s.ehrCardText}>
                                    <span className={s.ehrCardName}>{ehr.name}</span>
                                    <span className={s.ehrCardOk}>
                                        <CheckOutlined /> Connected
                                    </span>
                                </span>
                            </div>
                        );
                    }
                    return (
                        <button
                            key={ehr.name}
                            type="button"
                            className={s.ehrCard}
                            disabled={reconnectingEhr === ehr.name}
                            onClick={() => (status === 'error' ? handleReconnect(ehr.name) : openFlow(ehr.name))}
                        >
                            <EhrBadge ehr={ehrByName(ehr.name)} />
                            <span className={s.ehrCardText}>
                                <span className={s.ehrCardName}>{ehr.name}</span>
                                {status === 'error' && (
                                    <span className={s.ehrCardErr}>
                                        {reconnectingEhr === ehr.name
                                            ? 'Reconnecting…'
                                            : 'Connection lost — click to reconnect'}
                                    </span>
                                )}
                                {status === 'setup' && <span className={s.ehrCardUpcomingTag}>Setting up</span>}
                                {status === 'agent' && <span className={s.ehrCardUpcomingTag}>Connecting</span>}
                            </span>
                        </button>
                    );
                })}
                {/* 原型 V3：Upcoming 不再置灰，点击进入 Tell us about your EHR 并预填名称 */}
                {upcomingEhrs.map((ehr) => (
                    <button
                        key={ehr.name}
                        type="button"
                        className={s.ehrCard}
                        onClick={() => {
                            setCustomName(ehr.name);
                            setCustomOpen(true);
                        }}
                    >
                        <EhrBadge ehr={ehrByName(ehr.name)} />
                        <span className={s.ehrCardText}>
                            <span className={s.ehrCardName}>{ehr.name}</span>
                            <span className={s.ehrCardUpcomingTag}>Upcoming</span>
                        </span>
                    </button>
                ))}
                <button
                    type="button"
                    className={cx(s.ehrCard, s.ehrCardOther)}
                    onClick={() => {
                        setCustomName('');
                        setCustomOpen(true);
                    }}
                >
                    <span className={s.otherPlusIcon}>
                        <PlusOutlined />
                    </span>
                    <span className={s.ehrCardText}>
                        <span className={s.ehrCardName}>My EHR isn&apos;t listed</span>
                        <span className={s.ehrCardSub}>Tell us what you use</span>
                    </span>
                </button>
            </div>

            <div className={s.scopeTitleRow}>
                <div className={s.connTitle}>Which patients should Klarity bill for?</div>
                {scopeEditing ? (
                    <div className={s.scopeBtns}>
                        <Button onClick={cancelScopeEdit} disabled={scopeSaving}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={saveScopeEdit} loading={scopeSaving}>
                            Save changes
                        </Button>
                    </div>
                ) : (
                    <Button onClick={openScopeEdit}>Edit</Button>
                )}
            </div>
            {scopeEditing ? (
                <>
                    <ClaimScopeField />
                    {claimScope === 'tagged' && (
                        <p className={s.scopeTip}>
                            Tip: add the &quot;Klarity Billing&quot; tag to any patient in your EHR to include them.
                        </p>
                    )}
                </>
            ) : (
                <>
                    <Form.Item name={['ehr', 'claimScope']} noStyle>
                        <HiddenFormField />
                    </Form.Item>
                    <div className={s.scopeRead}>
                        <div className={s.scopeReadVal}>{currentScope.title}</div>
                        <div className={s.scopeReadDesc}>{currentScope.desc}</div>
                    </div>
                </>
            )}

            <div className={s.scopeTitleRow}>
                <div className={s.connTitle}>When should Klarity start processing claims?</div>
                {startEditing ? (
                    <div className={s.scopeBtns}>
                        <Button onClick={cancelStartEdit} disabled={startSaving}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={saveStartEdit} loading={startSaving}>
                            Save changes
                        </Button>
                    </div>
                ) : (
                    <Button onClick={openStartEdit}>Edit</Button>
                )}
            </div>
            {startEditing ? (
                <ClaimStartFields />
            ) : (
                <>
                    <Form.Item name={['ehr', 'claimStartMode']} noStyle>
                        <HiddenFormField />
                    </Form.Item>
                    <Form.Item name={['ehr', 'claimStartDate']} noStyle>
                        <HiddenFormField />
                    </Form.Item>
                    <div className={s.scopeRead}>
                        <div className={s.scopeReadVal}>{startReadTitle}</div>
                        <div className={s.scopeReadDesc}>{startReadDesc}</div>
                    </div>
                </>
            )}

            {flowEhr && (
                <EhrConnectFlow
                    ehrName={flowEhr}
                    open
                    reconnect={flowReconnect}
                    onClose={() => setFlowEhr(null)}
                    onSuccess={handleFlowSuccess}
                />
            )}
            <CustomEhrModal
                open={customOpen}
                onClose={() => setCustomOpen(false)}
                onSubmitted={handleCustomSubmitted}
                initialName={customName}
            />
        </div>
    );
}
